import type { IntlShape } from 'react-intl';
import type { SubscriptionIssueDto } from 'api/generated/types.gen';

/** For an issue this build has no specific wording for. Never silently nothing. */
const generic = (name: string, intl: IntlShape) => intl.formatMessage({
  id: "checkout.subscriptionIssue.generic",
  defaultMessage: [{
    "type": 0,
    "value": "‘"
  }, {
    "type": 1,
    "value": "name"
  }, {
    "type": 0,
    "value": "’ needs changing before this order can be paid for."
  }]
}, {
  name
});

/**
 * The wording follows the server's own refusals (`Conflict.SplitSubscription*`), so a buyer who
 * meets both reads the same explanation twice rather than two descriptions of one problem.
 *
 * An issue whose detail is missing is worded without it rather than around a blank: the row and
 * seat come from the ticket's `RowName`/`SeatName`, which unnumbered seating leaves empty.
 */
const describeSubscriptionIssue = (issue: SubscriptionIssueDto, intl: IntlShape): string => {
  const name = issue.subscriptionName ?? '';
  switch (issue.reason) {
    case 'BelowMinEvents':
      // The server only raises this with a minimum attached, and a sentence naming no number says
      // less than nothing.
      return issue.minEvents ? intl.formatMessage({
        id: "checkout.subscriptionIssue.belowMinEvents",
        defaultMessage: [{
          "type": 0,
          "value": "‘"
        }, {
          "type": 1,
          "value": "name"
        }, {
          "type": 0,
          "value": "’ needs tickets for at least "
        }, {
          "type": 6,
          "value": "min",
          "options": {
            "one": {
              "value": [{
                "type": 7
              }, {
                "type": 0,
                "value": " event"
              }]
            },
            "other": {
              "value": [{
                "type": 7
              }, {
                "type": 0,
                "value": " events"
              }]
            }
          },
          "offset": 0,
          "pluralType": "cardinal"
        }, {
          "type": 0,
          "value": "."
        }]
      }, {
        name,
        min: issue.minEvents
      }) : generic(name, intl);
    case 'UnevenEventTicketCount':
      return intl.formatMessage({
        id: "checkout.subscriptionIssue.unevenEventTicketCount",
        defaultMessage: [{
          "type": 0,
          "value": "‘"
        }, {
          "type": 1,
          "value": "name"
        }, {
          "type": 0,
          "value": "’ needs the same number of tickets on every event."
        }]
      }, {
        name
      });
    case 'SeatMismatch':
      return issue.requiredRow && issue.requiredSeat ? intl.formatMessage({
        id: "checkout.subscriptionIssue.seatMismatch",
        defaultMessage: [{
          "type": 0,
          "value": "‘"
        }, {
          "type": 1,
          "value": "name"
        }, {
          "type": 0,
          "value": "’ needs seat "
        }, {
          "type": 1,
          "value": "seat"
        }, {
          "type": 0,
          "value": " in row "
        }, {
          "type": 1,
          "value": "row"
        }, {
          "type": 0,
          "value": " on every event."
        }]
      }, {
        name,
        row: issue.requiredRow,
        seat: issue.requiredSeat
      }) : intl.formatMessage({
        id: "checkout.subscriptionIssue.seatMismatchUnnamed",
        defaultMessage: [{
          "type": 0,
          "value": "‘"
        }, {
          "type": 1,
          "value": "name"
        }, {
          "type": 0,
          "value": "’ needs the same seats on every event."
        }]
      }, {
        name
      });
    case 'SeatUnverifiable':
      return intl.formatMessage({
        id: "checkout.subscriptionIssue.seatUnverifiable",
        defaultMessage: [{
          "type": 0,
          "value": "We could not check the seats on ‘"
        }, {
          "type": 1,
          "value": "name"
        }, {
          "type": 0,
          "value": "’. Try changing its seats, or remove it to continue."
        }]
      }, {
        name
      });
    default:
      // buy-api and this app deploy separately, so a reason added on one side reaches the other
      // before it can be worded. Saying less is fine; saying nothing is not — an empty list reads
      // as a payable order, and the buyer would meet the server's refusal with no warning at all.
      return generic(name, intl);
  }
};

/**
 * One sentence per issue, in the order the backend reported them. One in, one out: the length is
 * what gates payment, so an issue that cannot be worded must still be counted.
 */
export const describeSubscriptionIssues = (issues: Array<SubscriptionIssueDto> | null | undefined, intl: IntlShape): string[] => (issues ?? []).map(issue => describeSubscriptionIssue(issue, intl));